import React, { useCallback, useMemo } from 'react';
import { BaseTextInputProps } from 'src/components/primitives/Input/types';
import { getAutoCompleteProps } from 'src/components/primitives/Input/utils';
import useSelectInnerText from 'src/hooks/useSelectInnerText';

type Params = Pick<
	BaseTextInputProps,
	| 'value'
	| 'onChange'
	| 'onEnter'
	| 'onEscape'
	| 'onKeyUp'
	| 'onClick'
	| 'onBlur'
	| 'onFocus'
	| 'validate'
	| 'autoComplete'
	| 'placeholder'
	| 'enableSelectAllOnFocus'
	| 'ref'
>;

/**
 * Returns props for a text input.
 *
 * Currently, our text fields don't need a lot of extra logic, so many of the
 * props are simply passed through for easier DX when using both this and
 * `useNumberInput` within the same component.
 */
export default function useTextInput({
	value,
	onChange: onChangeFromProps,
	onClick: onClickFromProps,
	onKeyUp: onKeyUpFromProps,
	onEnter,
	onEscape,
	onBlur: onBlurFromProps,
	onFocus,
	validate,
	autoComplete,
	placeholder,
	enableSelectAllOnFocus,
	ref,
}: Params) {
	const [selectInnerText, resetSelectInnerText] = useSelectInnerText();

	const onChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			// Skip any updates if the caller's eager validation fails.
			if (validate && !validate(e.target.value)) {
				return;
			}

			onChangeFromProps?.(e.target.value);
		},
		[onChangeFromProps, validate]
	);

	const onKeyUp = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			onKeyUpFromProps?.(event);

			if (event.key === 'Enter') {
				onEnter?.();
				return;
			}

			if (event.key === 'Escape') {
				onEscape?.();
				return;
			}
		},
		[onEnter, onEscape, onKeyUpFromProps]
	);

	const onClick = useCallback(
		(e: React.MouseEvent<HTMLInputElement>) => {
			if (enableSelectAllOnFocus) {
				selectInnerText(e);
			}

			onClickFromProps?.(e);
		},
		[enableSelectAllOnFocus, onClickFromProps, selectInnerText]
	);

	const onBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			if (enableSelectAllOnFocus) {
				resetSelectInnerText();
			}

			onBlurFromProps?.(e);
		},
		[enableSelectAllOnFocus, onBlurFromProps, resetSelectInnerText]
	);

	return useMemo(
		() => ({
			value,
			onChange,
			onKeyUp,
			onClick,
			onBlur,
			onFocus,
			placeholder,
			ref,
			...getAutoCompleteProps(autoComplete),
		}),
		[
			value,
			onChange,
			onKeyUp,
			onClick,
			onBlur,
			onFocus,
			placeholder,
			autoComplete,
			ref,
		]
	);
}
